import { useEffect, useMemo, useState } from 'react';
import type { ApiClient, Importabgleich, Importdetail, Importpunkt, Importtyp, Importvorschau } from '../../api';
import {
  DataTable, Drawer, ErrorState, Feld, FormSection, LoadingState, PropertyGrid, StatusBadge, formatiereZahl, spalteFuer,
  type Spalte,
} from '../../muster';
import {
  abgleichBedingungen, alteAuswahl, auswahlAus, eigeneMerkmale, typSchluessel, typText, verlangteMerkmale,
} from './datenregeln';

/** Was beim Öffnen vorbelegt ist: beim Aktualisieren Typ und gespeicherte Auswahl, beim Neu-Zuweisen nur der Typ. */
export interface Vorgabe {
  familie: string;
  typ: string;
  auswahl: string | null;
}

type Vorschauwert = Importvorschau['werte'][number];
type Treffer = Importabgleich['treffer'][number];

/** So viele Bauteile zeigt der Server im Abgleich; die Zahlen darüber gelten für alle. */
const TREFFERGRENZE = 60;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HERSTELLERDATEN ZUORDNEN: Ziel, Merkmale, Vorschau, übernehmen (Schubfach)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Herstellerwert ist nur mit seinen Bedingungen gültig:
 *
 *     Nenngröße 100, 36 m³/h, Spaltbreite 12 mm  →   9 Pa
 *     Nenngröße 100, 36 m³/h, Spaltbreite  8 mm  →  14 Pa
 *
 * Deshalb wird nichts übernommen, bevor die Merkmale festgelegt sind. Zwei Wege:
 * „Vorschau“ (eine Auswahl für alle Bauteile des Typs) und „Je Bauteil
 * abgleichen“ (jedes Bauteil über seine eigene Luftmenge - der fachlich
 * richtige Weg).
 *
 * TV6d: aus `seiten/Datenblattimporte.tsx` herausgelöst; die Auswahlfelder über
 * der Vorschau (vor TV6d „Zuweisungsmatrix“, TX-Ausnahme) sind Muster-Felder in
 * einer `FormSection`, die Tabellen `DataTable`. Die Aufrufe und die Auswahl
 * sind wörtlich die alten (`datenregeln.ts`, `Lueftungsdaten.schreibwege.test.tsx`).
 */
export function Zuordnen({ api, projektId, importId, vorgabe, schliessen }: {
  api: ApiClient;
  projektId: string;
  importId: string;
  vorgabe?: Vorgabe | null;
  schliessen: () => void;
}) {
  const [detail, setDetail] = useState<Importdetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [typen, setTypen] = useState<Importtyp[] | null>(null);
  const [familie, setFamilie] = useState(vorgabe?.familie ?? '');
  const [typ, setTyp] = useState(vorgabe?.typ ?? '');
  const [abgleich, setAbgleich] = useState<Importabgleich | null>(null);
  const [ueberschreibenAbgleich, setUeberschreibenAbgleich] = useState(false);

  // Die gespeicherte Auswahl, wenn eine vorliegt (JSON im Datenblatt, so wie der Abgleich sie hinterlegt hat).
  const alt = useMemo(() => alteAuswahl(vorgabe?.auswahl), [vorgabe]);

  const [sizeKey, setSizeKey] = useState(alt?.sizeKey ?? '');
  const [variantKey, setVariantKey] = useState('');
  const [tabelle, setTabelle] = useState(alt?.tabelle ?? '');
  const [punktIndex, setPunktIndex] = useState<number | null>(null);
  const [konfiguration, setKonfiguration] = useState('');
  const [eigeneWahl, setEigeneWahl] = useState<Record<string, string>>({});

  const [punkte, setPunkte] = useState<Importpunkt[] | null>(null);
  const [vorschau, setVorschau] = useState<Importvorschau | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState<string | null>(null);

  useEffect(() => {
    api.importDetail(importId).then(setDetail).catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
    api.importTypen(projektId).then(setTypen).catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, importId, projektId]);

  const gewaehlterTyp = typen?.find((t) => t.familie === familie && t.typ === typ);

  // Die Betriebspunkte erst laden, wenn Tabelle und Größe gewählt sind: Bei FK2-EU sind es 600.
  useEffect(() => {
    if (!tabelle) { setPunkte(null); return; }
    api.importPunkte(importId, tabelle, sizeKey || undefined)
      .then((a) => setPunkte(a.punkte))
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, importId, tabelle, sizeKey]);

  const auswahl = useMemo(
    () => auswahlAus(detail, { sizeKey, variantKey, tabelle, punktIndex, konfiguration, eigeneWahl }),
    [detail, sizeKey, variantKey, tabelle, punktIndex, konfiguration, eigeneWahl],
  );

  const zeigeVorschau = async () => {
    setLaeuft(true); setFehler(null); setFertig(null);
    try {
      setVorschau(await api.importVorschau(importId, projektId, familie, typ, auswahl));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setVorschau(null);
    } finally {
      setLaeuft(false);
    }
  };

  const zeigeAbgleich = async (schreiben: boolean) => {
    setLaeuft(true); setFehler(null); setFertig(null); setVorschau(null);
    try {
      // Die Bedingungen des gewählten Punkts (bei Z-LVS je Luftmenge drei Zeilen, eine je Spaltbreite), sonst die der
      // letzten Zuweisung.
      const bedingungen = abgleichBedingungen(punkte?.find((x) => x.index === punktIndex), alt);
      setAbgleich(await api.importAbgleich(
        importId, projektId, familie, typ, tabelle, sizeKey || null, bedingungen, schreiben, ueberschreibenAbgleich));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setAbgleich(null);
    } finally {
      setLaeuft(false);
    }
  };

  const uebernehmen = async (ueberschreiben: boolean) => {
    setLaeuft(true); setFehler(null);
    try {
      const a = await api.importUebernehmen(importId, projektId, familie, typ, auswahl, ueberschreiben);
      setFertig(`${a.uebernommen} Wert(e) übernommen` + (a.offen ? `, ${a.offen} ohne Auswahl bestimmbar.` : '.') + ' ' + a.message);
      setVorschau(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const kopf = vorgabe ? 'Aktualisieren' : 'Zuordnen';

  if (!detail) {
    return (
      <Drawer offen titel={kopf} beiSchliessen={schliessen} breite="720px"
        fuss={<button type="button" className="m-knopf" onClick={schliessen}>Schließen</button>}>
        {fehler
          ? <ErrorState klein titel="Das Dokument konnte nicht geladen werden." meldung={fehler} />
          : <LoadingState text="Das Dokument wird abgerufen …" zeilen={4} />}
      </Drawer>
    );
  }

  // Welche Merkmale braucht es? Das sagt das Dokument selbst, in den selection-Angaben seiner Zuordnungen.
  const braucht = verlangteMerkmale(detail);
  const eigeneFelder = eigeneMerkmale(detail);
  const gewaehlterPunkt = punkte?.find((p) => p.index === punktIndex);

  return (
    <Drawer
      offen
      titel={`${kopf}: ${detail.hersteller} ${detail.serie}`}
      beschreibung="Ziel festlegen, Merkmale wählen, dann Vorschau oder Abgleich je Bauteil - übernommen wird erst danach."
      beiSchliessen={schliessen}
      breite="720px"
      fuss={<button type="button" className="m-knopf" onClick={schliessen}>Schließen</button>}
    >
      {/* Die Zuordnung darf nie aus ähnlichen Namen geraten werden - sie wird gewählt, nicht getippt. */}
      <FormSection titel="Ziel im Projekt" spalten={1}>
        <Feld
          art="auswahl"
          beschriftung="Bauteiltyp im Projekt"
          breite="voll"
          deaktiviert={!!vorgabe || typen === null}
          platzhalter={typen === null ? 'Typen werden geladen …' : '— wählen —'}
          wert={familie && typ ? typSchluessel(familie, typ) : ''}
          optionen={(typen ?? []).map((t) => ({ wert: typSchluessel(t.familie, t.typ), beschriftung: typText(t) }))}
          beiAenderung={(w) => {
            const [f, t] = (w as string).split('\u0000');
            setFamilie(f ?? '');
            setTyp(t ?? '');
            setVorschau(null);
            setAbgleich(null);
          }}
          hinweis={gewaehlterTyp && gewaehlterTyp.mitLuftmenge === 0
            ? 'Kein Bauteil dieses Typs trägt eine Luftmenge. Der Abgleich je Bauteil ist damit nicht möglich - der Parameter Volumenstrom ist im Modell nicht gefüllt.'
            : undefined}
        />
      </FormSection>

      {/* Ohne die Merkmale ist kein Wert bestimmt. */}
      <FormSection titel="Produktmerkmale" spalten={2}>
        <Feld art="auswahl" beschriftung="Baugröße" platzhalter="— wählen —" wert={sizeKey}
          optionen={detail.groessen.map((g) => ({ wert: g.sizeKey ?? '', beschriftung: g.bezeichnung }))}
          beiAenderung={(w) => { setSizeKey(w as string); setPunktIndex(null); setVorschau(null); }} />

        {braucht.has('variantKey') && detail.varianten.length > 0 && (
          <Feld art="auswahl" beschriftung="Variante" platzhalter="— wählen —" wert={variantKey}
            optionen={detail.varianten.map((v) => ({ wert: v.variantKey, beschriftung: `${v.variantKey} — ${v.bezeichnung}` }))}
            beiAenderung={(w) => { setVariantKey(w as string); setVorschau(null); }} />
        )}

        {detail.tabellen.length > 0 && (
          <Feld art="auswahl" beschriftung="Tabelle" platzhalter="— keine —" wert={tabelle}
            optionen={detail.tabellen.map((t) => ({ wert: t.tableKey, beschriftung: `${t.tableKey} (${formatiereZahl(t.anzahl, 0)})` }))}
            beiAenderung={(w) => { setTabelle(w as string); setPunktIndex(null); setVorschau(null); }} />
        )}

        {eigeneFelder.map((z) => (
          <Feld key={z.fieldKey} art="auswahl" beschriftung={z.fieldKey} platzhalter="— wählen —" wert={eigeneWahl[z.fieldKey] ?? ''}
            optionen={(z.allowedValues ?? []).map((v) => ({ wert: String(v), beschriftung: String(v) }))}
            beiAenderung={(w) => { setEigeneWahl((e) => ({ ...e, [z.fieldKey]: w as string })); setVorschau(null); }} />
        ))}

        {/* Der Betriebspunkt trägt die Bedingungen im Namen - sonst wäre „36 m³/h“ bei Z-LVS dreimal derselbe Eintrag. */}
        {punkte && punkte.length > 0 && (
          <Feld art="auswahl" beschriftung={`Betriebspunkt (${formatiereZahl(punkte.length, 0)})`} breite="voll" platzhalter="— wählen —"
            wert={punktIndex === null ? '' : String(punktIndex)}
            optionen={punkte.map((p) => ({ wert: String(p.index), beschriftung: p.bezeichnung }))}
            beiAenderung={(w) => { setPunktIndex(w === '' ? null : Number(w)); setKonfiguration(''); setVorschau(null); }} />
        )}

        {gewaehlterPunkt && gewaehlterPunkt.konfigurationen.length > 0 && (
          <Feld art="auswahl" beschriftung="Konfiguration" breite="voll" platzhalter="— wählen —" wert={konfiguration}
            hinweis="Das Dokument führt mehrere Werte je Punkt."
            optionen={gewaehlterPunkt.konfigurationen.map((k) => ({ wert: k, beschriftung: k }))}
            beiAenderung={(w) => { setKonfiguration(w as string); setVorschau(null); }} />
        )}
      </FormSection>

      <div className="lue-speichern">
        <button type="button" className="m-knopf" disabled={laeuft || !familie || !typ} onClick={() => void zeigeVorschau()}
          title="Eine Auswahl für alle Bauteile dieses Typs">
          Vorschau
        </button>
        {/* Der fachlich richtige Weg: Jedes Bauteil hat seine eigene Luftmenge. */}
        <button type="button" className="m-knopf"
          disabled={laeuft || !familie || !typ || !tabelle || (gewaehlterTyp?.mitLuftmenge ?? 0) === 0}
          onClick={() => void zeigeAbgleich(false)}
          title={tabelle ? 'Jedes Bauteil über seine eigene Luftmenge zuordnen' : 'Erst eine Tabelle wählen'}>
          Je Bauteil abgleichen
        </button>
      </div>

      {fehler && <ErrorState klein titel="Das hat nicht geklappt." meldung={fehler} />}
      <p className="lue-meldung" role="status">{fertig ?? ''}</p>

      {abgleich && (
        <Abgleichstabelle abgleich={abgleich} laeuft={laeuft} ueberschreiben={ueberschreibenAbgleich}
          setzeUeberschreiben={setUeberschreibenAbgleich} uebernehmen={() => void zeigeAbgleich(true)} />
      )}

      {vorschau && <Vorschautabelle vorschau={vorschau} laeuft={laeuft} uebernehmen={(u) => void uebernehmen(u)} />}
    </Drawer>
  );
}

/** Eine Auswahl für alle Bauteile des Typs: was übernommen würde. Übernommen werden Vorschläge, keine Werte. */
function Vorschautabelle({ vorschau: v, laeuft, uebernehmen }: {
  vorschau: Importvorschau;
  laeuft: boolean;
  uebernehmen: (ueberschreiben: boolean) => void;
}) {
  const spalten: Spalte<Vorschauwert>[] = [
    { schluessel: 'feld', titel: 'Feld', immerSichtbar: true, wert: (w) => w.fieldKey },
    { schluessel: 'neu', titel: 'neu', wert: (w) => w.neu },
    {
      schluessel: 'bisher', titel: 'bisher', wert: (w) => (w.zustand === 'konflikt' ? w.bisher : w.zustand === 'unveraendert' ? 'gleich' : ''),
      inhalt: (w) => (w.zustand === 'konflikt'
        ? <><StatusBadge klein zustand="warnung" text="Konflikt" /> {w.bisher}</>
        : w.zustand === 'unveraendert' ? <span className="lue-leise">gleich</span> : null),
    },
    {
      schluessel: 'quelle', titel: 'Quelle', wert: (w) => w.quelle,
      inhalt: (w) => <><span>{w.quelle}</span>{w.bedingung && <span className="lue-zweitzeile">{w.bedingung}</span>}</>,
    },
  ];

  return (
    <section className="lue-abschnitt">
      <h3 className="lue-unterkopf">Vorschau</h3>
      <PropertyGrid spalten={3} dicht eintraege={[
        { beschriftung: 'betroffene Bauteile', wert: v.bauteile },
        { beschriftung: 'neu', wert: v.neu },
        { beschriftung: 'unverändert', wert: v.unveraendert },
        { beschriftung: 'Konflikte', wert: v.konflikte },
      ]} />

      {v.werte.length > 0 && (
        <DataTable name="Werte der Auswahl" spalten={spalten} zeilen={v.werte} schluessel={(w) => w.fieldKey}
          zeilenKlasse={(w) => (w.zustand === 'konflikt' ? 'datenfeld' : undefined)} suche={false} spaltenwahl={false} dicht />
      )}

      {/* Offene Felder sind kein Mangel, sondern eine Aussage: Die Auswahl bestimmt sie nicht, oder das Dokument nennt
          eine Regel in Worten. */}
      {v.offen.length > 0 && (
        <div className="lue-hinweisliste">
          <StatusBadge klein zustand="neutral" text={`${formatiereZahl(v.offen.length, 0)} Feld(er) nicht bestimmt`} />
          <ul>{v.offen.map((o) => <li key={o.fieldKey}><strong>{o.fieldKey}</strong> - {o.grund}</li>)}</ul>
        </div>
      )}

      <div className="lue-speichern">
        <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft || v.werte.length === 0} onClick={() => uebernehmen(false)}>
          {v.werte.length} Wert(e) übernehmen
        </button>
        {v.konflikte > 0 && (
          <button type="button" className="m-knopf" disabled={laeuft} onClick={() => uebernehmen(true)}>Mit Überschreiben übernehmen</button>
        )}
      </div>
      <p className="lue-leise">
        Übernommen werden <strong>Vorschläge</strong>, keine Werte. Die Bestätigung geschieht im Vorschlagsdialog am
        Bauteil, wo die Quellenangabe daneben steht.
      </p>
    </section>
  );
}

/**
 * Wie die Bauteile in das Raster der Herstellertabelle fallen. Interpoliert wird nur zwischen gedruckten Zeilen und so
 * gekennzeichnet; außerhalb bekommt das Bauteil das Produkt, aber keine luftmengenabhängigen Werte - dafür einen Befund.
 */
function Abgleichstabelle({ abgleich: a, laeuft, ueberschreiben, setzeUeberschreiben, uebernehmen }: {
  abgleich: Importabgleich;
  laeuft: boolean;
  ueberschreiben: boolean;
  setzeUeberschreiben: (b: boolean) => void;
  uebernehmen: () => void;
}) {
  const spalten: Spalte<Treffer>[] = [
    { schluessel: 'name', titel: 'Bauteil', immerSichtbar: true, wert: (t) => t.name },
    { schluessel: 'luftmenge', titel: 'Luftmenge', ...spalteFuer('volumenstrom_luft'), wert: (t) => t.luftmenge },
    {
      schluessel: 'art', titel: 'Zuordnung', art: 'status', wert: (t) => t.art,
      inhalt: (t) => (
        <>
          <StatusBadge klein zustand={t.art === 'genau' ? 'gut' : t.art === 'interpoliert' ? 'info' : 'warnung'}
            text={t.art === 'ohne_menge' ? 'keine Menge' : t.art} />
          <span className="lue-zweitzeile">{t.hinweis}</span>
        </>
      ),
    },
    {
      schluessel: 'werte', titel: 'Werte', sortierbar: false, wert: (t) => t.werte.map((w) => `${w.fieldKey}: ${w.wert}`).join(' · '),
      inhalt: (t) => (t.werte.length === 0 ? null : <span>{t.werte.map((w) => `${w.fieldKey}: ${w.wert}`).join(' · ')}</span>),
    },
  ];

  const befundlage = a.ausserhalb > 0 || a.ohneMenge === a.bauteile;

  return (
    <section className="lue-abschnitt">
      <h3 className="lue-unterkopf">Abgleich je Bauteil</h3>
      <PropertyGrid spalten={3} dicht eintraege={[
        { beschriftung: 'Bauteile', wert: a.bauteile },
        { beschriftung: 'genau', wert: a.genau },
        { beschriftung: 'interpoliert', wert: a.interpoliert },
        { beschriftung: 'außerhalb → Befund', wert: a.ausserhalb },
        ...(a.ohneMenge > 0 ? [{ beschriftung: 'ohne Luftmenge', wert: a.ohneMenge }] : []),
      ]} />

      <p className="lue-leise">
        {befundlage && <StatusBadge klein zustand="warnung" text="Befund" />} {a.hinweis}
      </p>

      {a.mengen.length > 0 && (
        <p className="lue-leise">
          Die Tabelle führt {formatiereZahl(a.zeilen, 0)} Zeile(n) mit den Luftmengen{' '}
          <strong>{a.mengen.map((m) => formatiereZahl(m, 0)).join(' · ')}</strong> m³/h - Quelle {a.quelle}.
          {a.felder.length > 0 && <> Gefüllt werden: <strong>{a.felder.join(', ')}</strong>.</>}
        </p>
      )}

      {a.treffer.length > 0 && (
        <DataTable
          name="Bauteile im Abgleich"
          spalten={spalten}
          zeilen={a.treffer}
          schluessel={(t) => t.id}
          zeilenKlasse={(t) => (t.art === 'genau' ? undefined : 'datenfeld')}
          trefferText={a.treffer.length >= TREFFERGRENZE
            ? `${formatiereZahl(a.treffer.length, 0)} von ${formatiereZahl(a.bauteile, 0)} Bauteilen gezeigt - die Zahlen oben gelten für alle`
            : undefined}
          dicht
        />
      )}

      {a.geschrieben > 0 ? (
        <p className="lue-meldung" role="status">
          {a.geschrieben} Bauteil(e) geschrieben.
          {a.ausserhalb > 0 && <> {a.ausserhalb} tragen einen Befund - unter <em>Bauteilbefunde</em> aufrufbar.</>}
        </p>
      ) : (
        <>
          <Feld art="haken" beschriftung="Vorhandene Werte überschreiben" wert={ueberschreiben}
            hinweis={'Von Hand gepflegte Werte bleiben immer unberührt. Interpolierte Werte bekommen die Herkunft '
              + '„interpoliert“ - sie sind eine begründete Annahme, keine Herstellerangabe.'
              + (a.ausserhalb > 0
                ? ` Die ${a.ausserhalb} Bauteile außerhalb der Tabelle bekommen das Produkt, aber keine luftmengenabhängigen Werte - dafür einen Befund.`
                : '')}
            beiAenderung={(w) => setzeUeberschreiben(w as boolean)} />
          <div className="lue-speichern">
            <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft || a.genau + a.interpoliert + a.ausserhalb === 0} onClick={uebernehmen}>
              Auf {a.genau + a.interpoliert} Bauteil(e) übernehmen{a.ausserhalb > 0 && `, ${a.ausserhalb} kennzeichnen`}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
